import { useEffect, useState } from 'react';
import { useMyReviews } from '@/features/professionals/hooks/useMyReviews';
import { MyReviewItem } from '@/features/professionals/components/MyReviewItem';
import type { MyReview } from '@/features/professionals/services/my-reviews.service';

export function RecensioniPage() {
  const [page, setPage] = useState(1);
  const [reviews, setReviews] = useState<MyReview[]>([]);

  const { data, isLoading, isFetching, isError } = useMyReviews(page);

  useEffect(() => {
    if (!data) return;
    // Ignore the prior page's payload that `keepPreviousData` still shows
    // while the next page is in flight.
    if (data.pagination.page !== page) return;
    // Replace this page's slice rather than blindly appending, so a refetch
    // of the currently-active page (e.g. after a reply/delete invalidates
    // the query) updates in place instead of being dropped as "already merged".
    const keepCount = (data.pagination.page - 1) * data.pagination.limit;
    setReviews((prev) => [...prev.slice(0, keepCount), ...data.reviews]);
  }, [data, page]);

  const hasMore = data?.pagination.hasMore ?? false;
  const isLoadingMore = isFetching && page > 1;

  return (
    <div>
      <div className="rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 px-6 py-5">
        <h1 className="text-xl font-bold text-white">Recensioni</h1>
        <p className="mt-1 text-sm text-white/70">Rispondi pubblicamente ai clienti che ti hanno recensito.</p>
      </div>

      <div className="mt-6 space-y-3">
        {isLoading && <p className="text-sm text-neutral-500">Caricamento…</p>}
        {isError && <p className="text-sm text-red-600">Errore nel caricamento.</p>}
        {!isLoading && reviews.length === 0 && (
          <p className="text-sm text-neutral-500">Nessuna recensione ancora.</p>
        )}
        {reviews.map((r) => <MyReviewItem key={r.id} review={r} />)}
        {hasMore && (
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoadingMore}
            className="text-sm font-medium text-brand-teal hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isLoadingMore ? 'Caricamento…' : 'Carica altre'}
          </button>
        )}
      </div>
    </div>
  );
}
